import React, {
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
} from 'react';
import { ApolloError, FetchResult, ApolloQueryResult } from '@apollo/client';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { AvenyContext } from '../../../../../AvenyContext';
import {
  formReducer,
  BusinessDataFormAction,
  IFormData,
  associatedAction,
  removeIdVersion,
  IValidateFn,
  IFieldValidations,
  IEditMenuAction,
  resolveRefs,
  mergeLoadedAndEditedValues,
  parseText,
} from './Utils/BusinessDataFormUtils';
import { INodeData } from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import { pipe, path, view, intersperse, init, last, isEmpty } from 'ramda';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import { useState } from 'react';
import { StandardRutinContext } from '../../../../_StandardRutin/StandardRutinContext';
import { ActiveRoutineContext } from '../../../../../Routing/ActiveRoutine/ActiveRoutineContext';
import { rutiner } from '../../../Const/Rutiner';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from './Utils/BusinessDataFormValueUtils';
import { traceReducer } from '../../../../../../Utils/debug';
import { MdDetailSelectionContext } from '../../../../../../Components/MasterDetail/Contexts/MdDetailSelectionContext';
import {
  composeLensDefs,
  composeLenses,
  idLens,
  LensDef,
  propsLens,
} from '../../../../../../Utils/lensUtils';
import { useBlocker } from 'react-router';

export type ActionTransformer = (
  action: Readonly<BusinessDataFormAction>,
  formState: IBusinessDataFormState
) => BusinessDataFormAction;

interface IBusinessDataFormProps<T extends INodeData> {
  isNew?: boolean;
  isEditing?: boolean;
  model: IClientBusinessModel;
  /** Formuläret editerar en association inom ett överliggande formulär */
  association?: string;
  /** Om associationen är "0 till många" så anges vilket associerat id som ska redigeras */
  associatedId?: string;
  data?: T | null | undefined;
  /** Funktion som anropar en mutation för att ändra i en befintlig post  */
  updateMutation?: (
    variables: IFormData
  ) => Promise<void | FetchResult<unknown>>;
  /** Funktion som anropar en mutation för att lägga till en ny post  */
  addMutation?: (variables: IFormData) => Promise<void | FetchResult<unknown>>;
  children: React.ReactNode;
  /** Formuläret används för att skapa ett nytt objekt */
  addNewObject?: boolean;
  mutationError?: ApolloError;
  mutationLoading?: boolean;
  dataLoading?: boolean;
  /** Formuläret visas över hela skärmen */
  fullScreen?: boolean;
  /** Körs om användaren avrbyter ändring/tillägg i formuläret */
  onCancel?: () => void;
  /** Körs om användaren sparat ändring/tillägg i formuläret */
  onValuesCommited?: () => void;
  formStateActionTransformer?: ActionTransformer;
  disableEditMode?: boolean;
  /** If true, changes to this form will not affect potential parent forms */
  newFormContext?: boolean;
  /** Kan sättas på ett underformulär. Ändringar sparas i det lokala formuläret. Man kan manuellt avbryta de lokala ändringarna eller spara till det överliggande formuläret */
  localValuesInChildForm?: boolean;

  initialValue?: IFormData;

  validateFns?: IValidateFn[];
  /** Används för att ange en egen lista av knappar som syns i formulärets Edit Menu. Defaultar till en Avbryt- och Spara-knapp */
  editMenuActions?: IEditMenuAction[];
  refetchData?: (variables?: any) => Promise<ApolloQueryResult<any>>;
  overrideChoiceButtons?: IOverrideChoiceButtons | undefined;
  /** Logga alla actions på formstate. Ska bara användas under utveckling. Kan sättas till en sträng som referens om man debuggar flera formulär samtidigt */
  trace?: string | true;
  customDataFormHasChanged?: (
    initialValues: IFormData | INodeData | null,
    values: IFormData
  ) => boolean;
}

interface IOverrideChoiceButtons {
  save?: IEditMenuAction;
  cancel?: IEditMenuAction;
  undo?: IEditMenuAction;
}

export interface IBusinessDataFormState<T = IFormData, CustomValues = unknown> {
  /** Om formuläret är i redigeringsläge, ui komponenter ska anpassa sig efter editMode,
   *  men det är inte en hård spärr för att skicka in värden till formuläret med kod */
  editMode: boolean;
  /** Värden som ska sparas, på ett lite annat format än loadedValues och currentValues som vi inte fångat i typningen än eftersom det ska skickas in till backend,
   *  kan innehålla Add_Values och Delete_Values  för poster som ska läggas till eller tas bort*/
  values: T;
  /** Värden som har skickats till backend, men där de skickade värdena inte lästs in som data ännu */
  committedValues?: T;
  /** Värden som inte lästs in som data, men som ändå hör till formuläret */
  customValues?: CustomValues;
  /** Värden som anvåändaren rört i formuläret, på ett format som values men istället för värden är fält som är touched true (och en path som är touched är truthy) */
  touched: IFormData;
  /** Inlästa värden, sätts med LOAD_DATA som i ett normalt formulär anropas när data ändras */
  loadedValues?: T;
  /** Aktuella värden, samma format som inlästa värden men uppdaterad med ändringar enligt values */
  currentValues?: T;
  /** values startar med initialValues för nya värden sätts sedan formHasChanged när values ändrats från initialValues */
  initialValues?: T;
  autoValues: T;
  editCancelled: boolean;
  commitVersion?: number;
  validation: IFieldValidations;
  formHasChanged: boolean;
  /** Inläsning av data är inte klar, ev. data är för prestandaoptimering och man kan inte lita på att den är komplett */
  loading: boolean;
  /** Should have value when new item was just added */
  addedId?: string;
}

export const InvalidValue = '%INVALID%';

interface ISaveFormOptions {
  suppressAddedIdSelection?: boolean;
}

type SaveFormType = (
  newValues?: IFormData,
  onSuccess?: () => void,
  onError?: () => void,
  options?: ISaveFormOptions
) => void;

export type BusinessDataFormUpdateType = (
  updateArray: FormUpdateAssociatonType[] | Array<FormUpdateAssociatonType[]>,
  ignoreFormHasChanged?: boolean
) => void;

export interface IDefinedBusinessDataFormContext<
  T = INodeData,
  CustomValues = unknown
> {
  formState: IBusinessDataFormState<T, CustomValues>;
  formStateDispatch: React.Dispatch<BusinessDataFormAction>;
  saveForm: SaveFormType;
  model: IClientBusinessModel;
  data?: T;
  error?: ApolloError;
  mutationLoading: boolean;
  dataLoading: boolean;
  fullScreen?: boolean;
  /** För att vi temporärt ska kunna disabla redigeringsläge för de ställen det inte är implementerat i ännu, kan tas bort när redigering funkar på alla business collections */
  disableEditMode: boolean;
  parentFormContext: IBusinessDataFormContext;
  openErrorDialog: boolean;
  closeErrorDialog: (open: boolean) => void;
  editMenuActions: IEditMenuAction[] | undefined;
  directEditMode: boolean;
  update: BusinessDataFormUpdateType;
  refetchData?: (variables?: any) => Promise<ApolloQueryResult<any>>;
  overrideChoiceButtons?: IOverrideChoiceButtons;
  isNew: boolean;
}
export type IBusinessDataFormContext<
  T = INodeData,
  CustomValues = unknown
> = IDefinedBusinessDataFormContext<T, CustomValues> | null;

const readInitialState = (directEditMode: boolean): IBusinessDataFormState => ({
  editMode: directEditMode,
  values: {},
  touched: {},
  autoValues: {},
  editCancelled: false,
  commitVersion: 0,
  validation: [],
  formHasChanged: false,
  loading: true,
});

const createInitialState: IBusinessDataFormState = {
  editMode: true,
  values: {},
  touched: {},
  autoValues: {},
  editCancelled: false,
  commitVersion: 0,
  validation: [],
  formHasChanged: false,
  loading: true,
};

export const BusinessDataFormContext =
  React.createContext<IBusinessDataFormContext<any>>(null);
BusinessDataFormContext.displayName = 'BusinessDataForm';

export const useBusinessDataFormContext: <
  T = INodeData,
  CustomValues = unknown
>() => IBusinessDataFormContext<T, CustomValues> = () =>
  useContext<IBusinessDataFormContext<any, any>>(BusinessDataFormContext);

/** Hämta BusinessDataFormContext för komponent som kräver att det finns. Kastar en exception om den används utanför en provider  */
export const useNonNullableBusinessDataFormContext =
  (): NonNullable<IBusinessDataFormContext> => {
    const context = useContext(BusinessDataFormContext);
    if (context == null) {
      throw new Error(
        'Komponenten kan inte användas utan en BusinessDataFormContext'
      );
    }
    return context;
  };

/** Skapa ett nytt tomt formContext, om t.ex. ett underformulär visas modalt och inte ska ärva den aktuella formContexten */
export const NewFormContext: React.FunctionComponent<
  React.PropsWithChildren<{}>
> = props => (
  <BusinessDataFormContext.Provider value={null}>
    {props.children}
  </BusinessDataFormContext.Provider>
);

const BusinessDataForm: <T extends INodeData>(
  props: IBusinessDataFormProps<T>
) => React.ReactElement<any> = props => {
  const { addMutation, updateMutation, onValuesCommited } = props;
  const avenyContext = useContext(AvenyContext);
  const { setFormHasChanged } = avenyContext;
  const modelContext = useContext(ModelContext);
  const parentFormContext = useContext(BusinessDataFormContext);
  const parentFormStateDispatch = parentFormContext?.formStateDispatch;
  const hasParentFormContext = !!parentFormContext;
  const associationpath = props.association
    ? [
        '_Associations',
        ...intersperse<LensDef>(
          '_Associations',
          (props.association ?? '')
            ?.split('.')
            .map(pathPart => propsLens(pathPart, pathPart + '_All'))
        ),
      ]
    : [];
  const associationLens = composeLensDefs(...associationpath);

  const dataLens = props.associatedId
    ? composeLenses(associationLens, idLens(props.associatedId))
    : associationLens;

  const data = useMemo<INodeData | null | undefined>(
    () =>
      props.data ??
      (parentFormContext?.formState.values && props.localValuesInChildForm //om parentform har ändringar och ändringar ska hållas lokalt så får ev. ändringar i parentform tas med i data
        ? view(
            dataLens,
            mergeLoadedAndEditedValues(
              parentFormContext?.data,
              parseText(parentFormContext.formState.values)
            )
          )
        : view(dataLens, parentFormContext?.data)),
    [
      dataLens,
      parentFormContext?.data,
      parentFormContext?.formState.values,
      props.data,
      props.localValuesInChildForm,
    ]
  );
  const standardRutinContext = useContext(StandardRutinContext);
  const activeRoutine = useContext(ActiveRoutineContext);
  const mdDetailSelectionContext = useContext(MdDetailSelectionContext);
  const directEditMode =
    !!rutiner[activeRoutine?.rutinInfo.rutinKod || '']?.directEditMode &&
    !props.disableEditMode;

  const [open, setOpen] = useState(false);

  const closeErrorDialog = (error: boolean) => {
    setOpen(error);
  };

  const loading =
    props.mutationLoading !== undefined
      ? props.mutationLoading
      : (parentFormContext && parentFormContext.mutationLoading) || false;

  const baseReducer = formReducer(
    props.model,
    modelContext.avenyCollectionMap,
    props.isNew ?? false,
    data ?? null,
    props.validateFns,
    directEditMode,
    props.customDataFormHasChanged,
    props.formStateActionTransformer
  );

  const reducer = props.trace
    ? traceReducer(
        baseReducer,
        props.trace === true ? undefined : props.trace,
        props.formStateActionTransformer,
        'transformedAction'
      )
    : baseReducer;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedReducer = useCallback(reducer, [
    data,
    props.isNew,
    props.validateFns,
    directEditMode,
    props.formStateActionTransformer,
  ]);

  let formState: IBusinessDataFormState<IFormData>;
  let formStateDispatch: React.Dispatch<BusinessDataFormAction>;
  const [localFormState, localFormStateDispatch] = useReducer(
    memoizedReducer,
    props.addNewObject || props.isNew || props.isEditing
      ? createInitialState
      : readInitialState(directEditMode)
  );
  formState = useMemo(() => {
    if (!parentFormContext || props.newFormContext) {
      //inget state ska hämtas från parentform
      return localFormState;
    } else {
      if (props.association == undefined && !props.localValuesInChildForm) {
        //samma formstate som parentform
        return parentFormContext.formState;
      } else {
        //lokalt state och parentform ska mergas
        const pathParts = props.association?.split('.') ?? [];
        const valuesPath1 = [
          ...init(pathParts).map(s => s + '_Values'),
          last(pathParts) +
            (!props.associatedId
              ? '_Values'
              : props.associatedId?.startsWith('New')
              ? '_Add_Values'
              : '_Update_Values'),
        ];

        const valuesPath = [
          '_Associations',
          ...intersperse('_Associations', valuesPath1),
        ];
        let parentValues: any =
          path(valuesPath, parentFormContext.formState.values) ?? {};

        if (props.associatedId) {
          parentValues = path<IFormData>(
            valuesPath,
            parentFormContext.formState.values
          )?.find((val: any) => val.id === props.associatedId);
        }
        const parentCurrentValues =
          parentFormContext.formState.currentValues &&
          (view<IFormData, IFormData>(
            dataLens,
            parentFormContext.formState.currentValues
          ) ??
            {});

        const parentValidation =
          parentFormContext.formState.validation?.reduce<IFieldValidations>(
            (res, val) => {
              if (
                val.path &&
                val.path.length >= 2 &&
                val.path[0] === props.association &&
                val.path[1] === props.associatedId
              ) {
                const newPath = val.path.slice(2);
                res.push({
                  ...val,
                  path: !isEmpty(newPath) ? newPath : undefined,
                });
              }
              return res;
            },
            []
          );

        return {
          ...parentFormContext.formState,
          values: props.localValuesInChildForm
            ? localFormState.values
            : parentValues,
          currentValues: props.localValuesInChildForm
            ? parentCurrentValues &&
              mergeLoadedAndEditedValues(
                parentCurrentValues,
                parseText(localFormState.values)
              )
            : parentCurrentValues,
          validation: props.localValuesInChildForm
            ? localFormState.validation
            : parentValidation,
          formHasChanged: props.localValuesInChildForm
            ? localFormState.formHasChanged
            : parentFormContext.formState.formHasChanged,
          touched: props.localValuesInChildForm
            ? localFormState.touched
            : parentFormContext.formState.touched,
        };
      }
    }
  }, [
    parentFormContext,
    props.newFormContext,
    props.association,
    props.associatedId,
    props.localValuesInChildForm,
    localFormState,
    dataLens,
  ]);

  formStateDispatch = useMemo(() => {
    if (
      hasParentFormContext &&
      parentFormStateDispatch &&
      !(props.newFormContext || props.localValuesInChildForm)
    ) {
      if (props.association !== undefined) {
        return pipe(
          associatedAction(
            props.association,
            props.associatedId,
            parentFormContext.data
          ),
          action => {
            if (action != null) {
              parentFormStateDispatch?.(action);
            }
          }
        );
      } else {
        return parentFormStateDispatch;
      }
    }
    return localFormStateDispatch;
  }, [
    localFormStateDispatch,
    hasParentFormContext,
    parentFormStateDispatch,
    parentFormContext?.data,
    props.associatedId,
    props.association,
    props.newFormContext,
    props.localValuesInChildForm,
  ]);

  //inläsning av data
  useEffect(() => {
    if (!(data == null) && data !== formState.loadedValues) {
      //om vi får ny data så laddas den och loading uppdateras
      //loading kan vara true om uppdateringen kommer från cache, men en nätverksuppdatering är på gång
      formStateDispatch({
        type: 'LOAD_DATA',
        data: data,
        loading: props.dataLoading,
      });
    } else if (formState.loading !== props.dataLoading) {
      //om loading uppdateras utan ny data (t.ex. när man börjar hämta ny data)
      formStateDispatch({
        type: 'SET_LOADING',
        value: !!props.dataLoading,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, props.dataLoading]);

  //registrera initialValues
  useEffect(() => {
    if (formState.editMode && props.isNew && props.initialValue !== undefined) {
      formStateDispatch({
        type: 'LOAD_INITIAL_VALUE',
        initialValue: props.initialValue,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValue]);

  //ändring av formHasChanged
  useEffect(() => {
    if (!hasParentFormContext) {
      setFormHasChanged(formState.formHasChanged);
    }
    return () => {
      if (!hasParentFormContext) {
        setFormHasChanged(false);
      }
    };
  }, [setFormHasChanged, formState.formHasChanged, hasParentFormContext]);

  //editCancelled
  useEffect(() => {
    if (formState.editCancelled && props.isNew) {
      mdDetailSelectionContext.unselectDetail?.();
    }
    if (formState.editCancelled && props.onCancel) {
      props.onCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.editCancelled]);

  //om vi har lokala ändringar i ett underformulär så ska de också köra cancel om huvudformuläret gör det
  useEffect(() => {
    if (
      parentFormContext?.formState.editCancelled &&
      props.localValuesInChildForm
    ) {
      formStateDispatch({ type: 'CANCEL' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentFormContext?.formState.editCancelled]);

  //post-commit callbacks
  useEffect(() => {
    if ((formState.commitVersion ?? 0) > 0 && onValuesCommited) {
      onValuesCommited();
    }
  }, [formState.commitVersion, onValuesCommited]);

  const saveForm: SaveFormType = useCallback(
    (newValues, onSuccess, onError, options) => {
      const values =
        newValues != null
          ? resolveRefs(newValues)
          : resolveRefs(formState.values);

      if (props.isNew && addMutation) {
        addMutation?.(removeIdVersion(values))
          .then(value => {
            if (typeof value === 'object') {
              if (value.errors === undefined || value.errors.length === 0) {
                const addedId: string | undefined = (value as FetchResult<any>)
                  ?.data?.db?.[`${props.model.CollectionName}_Add`]?.id;

                formStateDispatch({
                  type: 'VALUES_COMMITED',
                  addedId,
                });
                onSuccess?.();

                if (
                  addedId &&
                  !parentFormContext &&
                  !options?.suppressAddedIdSelection
                ) {
                  mdDetailSelectionContext.selectDetail?.(addedId, true);
                }
              } else {
                setOpen(true);
                onError?.();
              }
            } else {
              setOpen(true);
              onError?.();
            }
          })
          .catch(e => {
            setOpen(true);
            onError?.();
          });
      } else if (props.isNew) {
        //om isNew men addMutation saknades
        throw new Error(
          'Det går inte att spara en ny post. Addmutation saknas'
        );
      } else if (updateMutation) {
        updateMutation?.(values)
          .then(value => {
            if (typeof value === 'object') {
              if (value.errors === undefined || value.errors.length === 0) {
                formStateDispatch({ type: 'VALUES_COMMITED' });
                onSuccess?.();
              } else {
                setOpen(true);
                onError?.();
              }
            } else {
              setOpen(true);
              onError?.();
            }
          })
          .catch(e => {
            setOpen(true);
            onError?.();
          });
      }
    },
    [
      formState.values,
      props.isNew,
      addMutation,
      updateMutation,
      props.model.CollectionName,
      formStateDispatch,
      mdDetailSelectionContext,
      parentFormContext,
    ]
  );

  const update = useCallback(
    (
      updateArray:
        | FormUpdateAssociatonType[]
        | Array<FormUpdateAssociatonType[]>,
      ignoreFormHasChanged?: boolean
    ): void => {
      formStateDispatch({
        type: 'UPDATE',
        value: getFormUpdateValue(updateArray, data ?? {}),
        ignoreFormHasChanged,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  if (standardRutinContext.formStateRef && parentFormContext == null) {
    standardRutinContext.formStateRef.current = {
      formState,
      formStateDispatch,
      saveForm,
      model: props.model,
      data: data,
      error: props.mutationError,
      fullScreen: props.fullScreen,
      mutationLoading: loading,
      dataLoading: props.dataLoading ?? false,
      disableEditMode: Boolean(props.disableEditMode),
      parentFormContext: parentFormContext,
      openErrorDialog: open,
      closeErrorDialog,
      editMenuActions: props.editMenuActions,
      directEditMode: Boolean(directEditMode),
      update,
      overrideChoiceButtons: props.overrideChoiceButtons,
      isNew: props.isNew ?? false,
    };
  }

  useEffect(() => {
    return () => {
      if (standardRutinContext.formStateRef && parentFormContext == null) {
        standardRutinContext.formStateRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(
    () => ({
      formState,
      formStateDispatch,
      saveForm,
      model: props.model,
      data: data,
      error: props.mutationError,
      fullScreen: props.fullScreen,
      mutationLoading: loading,
      disableEditMode: Boolean(props.disableEditMode),
      parentFormContext: parentFormContext,
      openErrorDialog: open,
      closeErrorDialog,
      editMenuActions: props.editMenuActions,
      directEditMode: Boolean(directEditMode),
      update,
      refetchData: props.refetchData,
      dataLoading: props.dataLoading ?? false,
      overrideChoiceButtons: props.overrideChoiceButtons,
      isNew: props.isNew ?? false,
      isEditing: props.isEditing ?? false,
    }),
    [
      formState,
      formStateDispatch,
      saveForm,
      props.model,
      props.mutationError,
      props.fullScreen,
      props.disableEditMode,
      props.editMenuActions,
      props.refetchData,
      props.dataLoading,
      props.overrideChoiceButtons,
      props.isNew,
      props.isEditing,
      data,
      loading,
      parentFormContext,
      open,
      directEditMode,
      update,
    ]
  );

  return (
    <BusinessDataFormContext.Provider value={contextValue}>
      {!parentFormContext && <NavigationBlocker />}
      {props.children}
    </BusinessDataFormContext.Provider>
  );
};

const NavigationBlocker: React.FC = () => {
  const avenyContext = useContext(AvenyContext);
  const standardRutinContext = useContext(StandardRutinContext);

  const blocker = useBlocker(({ nextLocation }) => {
    return (
      Boolean(
        standardRutinContext?.formStateRef?.current?.formState.formHasChanged
      ) &&
      !(nextLocation.state as { disableOpenChoiceDialog?: boolean } | null)
        ?.disableOpenChoiceDialog
    );
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      avenyContext.setOpenChoiceDialog(true, () => {
        blocker.proceed();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);

  useEffect(() => {
    if (!avenyContext.openChoiceDialog && blocker.state === 'blocked') {
      blocker.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avenyContext.openChoiceDialog]);

  return null;
};

export default BusinessDataForm;
